import { Dialog } from '@headlessui/react'

import { Button } from 'components/Button'
import { Modal } from 'components/Modal'
import { PhoneLayout } from 'components/PhoneLayout'
import type { TSurveyBuilderQuestion } from './builder'

type Props = {
  open: boolean
  onClose: () => void
  questions: TSurveyBuilderQuestion[]
}

// male回答画面（line-ban-male/src/pages/Survey）の配色を再現するための固定値。
// management側のテーマ色とは異なるため、あえてハードコードしている。
const choiceRowClassName =
  'flex items-center gap-3 rounded-xl border-2 border-[#E7D9C9]/70 bg-white px-4 py-3'

const choiceMarkClassName = 'h-5 w-5 shrink-0 border border-[#C9C4BE] bg-white'

const textFieldClassName =
  'w-full rounded-xl border border-[#E7D9C9]/80 bg-white px-3 py-2.5 text-base text-[#5B5856]/70 shadow-sm'

function QuestionPreviewCard({
  question,
  questionNumber,
}: {
  question: TSurveyBuilderQuestion
  questionNumber: number
}) {
  const title = question.title.trim() || '（設問文未入力）'
  return (
    <div className="space-y-3 rounded-2xl border border-[#E7D9C9]/70 bg-white p-4 shadow-sm">
      {question.imageUrl ? (
        <img
          src={question.imageUrl}
          alt=""
          data-testid="survey-preview-question-image"
          className={`w-full rounded object-cover ${
            question.imageAspectRatio === '1:1'
              ? 'aspect-square'
              : 'aspect-video'
          }`}
        />
      ) : null}
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-[#14110F]">
          <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#48A9DE] align-middle text-xs font-bold text-white">
            {questionNumber}
          </span>
          {title}
        </p>
        <span className="flex shrink-0 items-center gap-1.5">
          {question.required ? (
            <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-600">
              必須
            </span>
          ) : (
            <span className="rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-semibold text-[#334155]">
              任意
            </span>
          )}
        </span>
      </div>
      {question.type === 'MULTI' && question.maxSelections ? (
        <p className="text-sm text-[#5B5856]">
          最大{question.maxSelections}個まで選択できます
        </p>
      ) : null}
      {question.type === 'SINGLE' || question.type === 'MULTI' ? (
        <div className="space-y-2">
          {(question.choices ?? []).map((choice) => (
            <div key={choice.clientId} className={choiceRowClassName}>
              <span
                className={`${choiceMarkClassName} ${
                  question.type === 'SINGLE' ? 'rounded-full' : 'rounded'
                }`}
              />
              <span className="text-base text-[#14110F]">
                {choice.label.trim() || '（選択肢未入力）'}
              </span>
            </div>
          ))}
          {question.type === 'SINGLE' && !question.required ? (
            <div className={choiceRowClassName}>
              <span className={`${choiceMarkClassName} rounded-full`} />
              <span className="text-base text-[#5B5856]">選択しない</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {question.type === 'TEXT' ? (
        <div className={`${textFieldClassName} min-h-[104px]`}>
          こちらにご記入ください
        </div>
      ) : null}
      {question.type === 'DATE' ? (
        <div className={textFieldClassName}>日付を選択</div>
      ) : null}
    </div>
  )
}

export function SurveyPreviewModal({ open, onClose, questions }: Props) {
  return (
    <Modal open={open} onClose={onClose} size="lg">
      <div className="grid gap-4">
        <div>
          <Dialog.Title as="h2" className="text-lg font-bold text-ink">
            回答画面プレビュー
          </Dialog.Title>
          <p className="mt-1 text-sm text-muted">
            LINEユーザーに表示される回答画面のイメージです。保存前の編集内容も反映されます。
          </p>
        </div>
        <div className="h-[540px]">
          <PhoneLayout title="アンケート" bodyClassName="bg-white">
            <div
              className="pointer-events-none mx-auto max-w-lg space-y-4"
              data-testid="survey-preview-phone"
            >
              <div className="space-y-1 pt-2">
                <p className="text-xl font-semibold text-[#14110F]">
                  アンケート
                </p>
                <p className="text-sm text-[#5B5856]">
                  全{questions.length}
                  問です。「送信する」を押すと回答が確定します。
                </p>
              </div>
              {questions.map((question, index) => (
                <QuestionPreviewCard
                  key={question.clientId}
                  question={question}
                  questionNumber={index + 1}
                />
              ))}
              <div className="pb-4 pt-2">
                <div className="w-full rounded-full bg-[#06C755] px-4 py-3.5 text-center text-base font-semibold text-white shadow-sm">
                  送信する
                </div>
              </div>
            </div>
          </PhoneLayout>
        </div>
        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </div>
    </Modal>
  )
}
